import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Diálogo modal do próprio cockpit.
 *
 * Substitui `prompt()` e `confirm()` do navegador: além de feios, alguns navegadores os
 * bloqueiam sem avisar — e uma recusa de curadoria que acontece sem o motivo registrado
 * derruba a justificativa da devolução do sinal.
 */
interface Props {
  titulo: string;
  children?: ReactNode;
  /** Quando presente, o diálogo pede um texto e só libera com o mínimo de caracteres. */
  campo?: { rotulo: string; obrigatorio?: boolean; minimo?: number; placeholder?: string };
  rotuloConfirmar: string;
  tomConfirmar?: 'normal' | 'perigo';
  aoConfirmar: (texto: string) => void;
  aoCancelar: () => void;
  ocupado?: boolean;
}

export function Dialogo({
  titulo,
  children,
  campo,
  rotuloConfirmar,
  tomConfirmar = 'normal',
  aoConfirmar,
  aoCancelar,
  ocupado,
}: Props) {
  const [texto, setTexto] = useState('');
  const caixa = useRef<HTMLDivElement>(null);

  // Fechar com Esc é o que todo mundo tenta primeiro.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoCancelar();
    };
    document.addEventListener('keydown', aoTeclar);
    caixa.current?.querySelector('textarea,input,button')?.scrollIntoView({ block: 'nearest' });
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aoCancelar]);

  const minimo = campo?.minimo ?? 3;
  const faltaTexto = campo?.obrigatorio && texto.trim().length < minimo;

  return (
    <div
      className="fundo-modal"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onClick={(e) => {
        if (e.target === e.currentTarget) aoCancelar();
      }}
    >
      <div className="modal" ref={caixa}>
        <h3>{titulo}</h3>
        {children && <div className="modal-corpo">{children}</div>}

        {campo && (
          <div className="campo" style={{ marginTop: 14 }}>
            <label htmlFor="modal-campo">{campo.rotulo}</label>
            <textarea
              id="modal-campo"
              rows={3}
              value={texto}
              placeholder={campo.placeholder}
              onChange={(e) => setTexto(e.target.value)}
              autoFocus
            />
            {faltaTexto && (
              <div className="dica">
                Mínimo de {minimo} caracteres — este texto fica registrado.
              </div>
            )}
          </div>
        )}

        <div className="modal-acoes">
          <button className="bt vazado pequeno" onClick={aoCancelar} disabled={ocupado}>
            Cancelar
          </button>
          <button
            className={`bt pequeno${tomConfirmar === 'perigo' ? ' perigo' : ''}`}
            disabled={ocupado || faltaTexto}
            onClick={() => aoConfirmar(texto.trim())}
          >
            {ocupado ? 'Aguarde…' : rotuloConfirmar}
          </button>
        </div>
      </div>
    </div>
  );
}
